import { useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Card,
    Columns,
    Container,
    Heading,
    Level,
    Section
} from "react-bulma-components";
import ChannelConnection from "../../js/connection/ChannelConnection";
import { useNavigate, useParams } from "react-router";
import useChannelValidation from "./hooks/useChannelValidation";
import LoadingIndicator from "../LoadingIndicator";
import SourceInputDropdown from "./SourceInputDropdown";
import liveswitch from "fm.liveswitch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

export default function ChannelValidationPage({
    connection,
    onConfirm
}: {
    connection: ChannelConnection;
    onConfirm: (lm: liveswitch.LocalMedia) => void;
}) {
    const channelId = useRef(useParams().id);
    const navigate = useNavigate();
    // { loading, error } = useChannelValidation(connection);
    useChannelValidation(connection)
    const loading = false;
    const error = "";
    
    const hasAccess = true;//useMemo(() => !loading && !error, [loading, error]);

    const [localMedia, setLocalMedia] = useState<liveswitch.LocalMedia>();
    const [videoInputs, setVideoInputs] = useState<liveswitch.SourceInput[]>(
        []
    );
    const [audioInputs, setAudioInputs] = useState<liveswitch.SourceInput[]>(
        []
    );

    useEffect(() => {
        // if (!hasAccess) {
        //     return;
        // }

        async function startLocalMedia() {
            const videoConfig = ChannelConnection.localMediaConfig;
            const lm = new liveswitch.LocalMedia(true, videoConfig, false);

            setLocalMedia(await lm.start());

            const videoDom = document.getElementById(
                "video-preview"
            ) as HTMLElement;
            const layoutManager = new liveswitch.DomLayoutManager(videoDom);
            layoutManager.setLocalView(lm.getView());
        }
        startLocalMedia();
    }, [hasAccess]);

    useEffect(() => {
        if (!localMedia) {
            return;
        }

        async function getDevices() {
            const videoSourceInputs = await localMedia!.getVideoSourceInputs();
            setVideoInputs(videoSourceInputs);

            const audioSourceInputs = await localMedia!.getAudioSourceInputs();
            setAudioInputs(audioSourceInputs);
        }
        getDevices();
    }, [localMedia]);

    function renderChannelAccess() {
        return (
            <Columns centered vCentered>
                {loading && (
                    <Columns.Column size={4}>
                        <Card className="validation-card">
                            <Card.Header>
                            <Card.Header.Title>
                                <Heading subtitle>Verifying Access...</Heading>
                            </Card.Header.Title>
                            </Card.Header>
                            <Card.Content>
                                <LoadingIndicator />
                            </Card.Content>
                        </Card>
                    </Columns.Column>
                )}

                {!loading && error && (
                    <Columns.Column size={4}>
                        <Card className="validation-card">
                            <Card.Header>
                            <Card.Header.Title>
                                    <Heading subtitle>An Error Occurred</Heading>
                            </Card.Header.Title>
                            </Card.Header>
                            <Card.Content>
                                <Heading size={4}>{error}</Heading>
                                <Button
                                    className="return-button"
                                    rounded
                                    onClick={() =>
                                        navigate("/", { replace: true })
                                    }>
                                    Return To Dashboard
                                </Button>
                            </Card.Content>
                        </Card>
                    </Columns.Column>
                )}
            </Columns>
        );
    }

    function renderDeviceSelection() {
        return (
            <Container>
                <Card className="device-selection-card">
                    <Card.Content>
                        <Columns>
                            <Columns.Column size="two-thirds">
                                <div
                                    id="video-preview"
                                    className="video-preview"
                                />
                            </Columns.Column>
                            <Columns.Column className="device-controls">
                                <Heading subtitle>Select Input Devices</Heading>

                                {videoInputs.length > 0 && (
                                    <SourceInputDropdown
                                        label="Video Input Source"
                                        inputs={videoInputs}
                                        defaultValue={videoInputs[0].getId()}
                                        onChange={async (input) => {
                                            await localMedia?.changeVideoSourceInput(
                                                input
                                            );
                                        }}
                                    />
                                )}

                                {audioInputs.length > 0 && (
                                    <SourceInputDropdown
                                        label="Audio Input Source"
                                        inputs={audioInputs}
                                        defaultValue={audioInputs[0].getId()}
                                        onChange={async (input) => {
                                            await localMedia?.changeAudioSourceInput(
                                                input
                                            );
                                        }}
                                    />
                                )}

                                <Button
                                    className="join-button"
                                    mt="auto"
                                    rounded
                                    fullwidth
                                    color="link"
                                    onClick={() => onConfirm(localMedia!)}>
                                    Join
                                </Button>
                            </Columns.Column>
                        </Columns>
                    </Card.Content>
                </Card>
            </Container>
        );
    }

    return (
        <Section className="validation-section">
            <Level className="header-level">
                <Level.Side align="left">
                    <Level.Item>
                        <Button className="back-button" rounded onClick={() => navigate("/")}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </Button>
                    </Level.Item>
                </Level.Side>
                <Level.Side align="left">
                    <Level.Item>
                        <div className="header-content">
                        <Heading>Channel {channelId.current}</Heading>
                        </div>
                    </Level.Item>
                </Level.Side>
                <Level.Side>
                    <Level.Item>
                        {/* Empty side for balance */}
                    </Level.Item>
                </Level.Side>
            </Level>
            {renderChannelAccess()}

            {hasAccess && renderDeviceSelection()}

            <style>{`
                .validation-section {
                    padding: 1rem;
                    background-color: #f2f2f7;
                    min-height: 100vh;
                }

                .header-level {
                    margin-bottom: 1.5rem;
                    position: relative;
                    display: flex;
                    align-items: center;
                    padding: 0.5rem 0;
                }

                .header-content {
                    text-align: left;
                    margin-left: 0.75rem;
                }

                .header-content .heading {
                    margin-bottom: 0;
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #000;
                }

                .back-button {
                    background: #ffffff;
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    color: #000;
                    width: 2.5rem;
                    height: 2.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    padding: 0;
                    border-radius: 50%;
                }

                .back-button:hover {
                    background: #ffffff;
                    border-color: rgba(0, 0, 0, 0.2);
                    opacity: 1;
                    transform: none;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }

                .validation-card {
                    background: #ffffff;
                    border-radius: 10px;
                    box-shadow: none;
                    border: none;
                    margin-bottom: 1rem;
                }

                .device-selection-card {
                    background: #ffffff;
                    border-radius: 10px;
                    box-shadow: none;
                    border: none;
                }

                .video-preview {
                    height: 60vh;
                    background-color: #000;
                    border-radius: 10px;
                    overflow: hidden;
                }

                .device-controls {
                    display: flex;
                    flex-direction: column;
                    gap: 1.25rem;
                    padding: 1rem;
                }

                .device-controls .heading {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #000;
                    margin-bottom: 0.5rem;
                }

                .join-button {
                    background: #007AFF;
                    border: none;
                    color: #ffffff;
                    height: 2.75rem;
                    font-weight: 600;
                    transition: opacity 0.2s ease;
                    border-radius: 10px;
                    margin-top: 1rem;
                }

                .join-button:hover {
                    background: #007AFF;
                    opacity: 0.9;
                    transform: none;
                    box-shadow: none;
                }

                .return-button {
                    background: #007AFF;
                    border: none;
                    color: #ffffff;
                    height: 2.75rem;
                    font-weight: 600;
                    transition: opacity 0.2s ease;
                    border-radius: 10px;
                }

                .return-button:hover {
                    background: #007AFF;
                    opacity: 0.9;
                    transform: none;
                    box-shadow: none;
                }

                @media (max-width: 768px) {
                    .validation-section {
                        padding: 0.75rem;
                    }

                    .header-level {
                        padding: 0.5rem 0;
                    }

                    .header-content {
                        margin-left: 0.5rem;
                    }

                    .header-content .heading {
                        font-size: 1.125rem;
                    }

                    .video-preview {
                        height: 40vh;
                        margin-bottom: 1rem;
                    }

                    .device-controls {
                        gap: 1rem;
                        padding: 0.75rem;
                    }
                }
            `}</style>
        </Section>
    );
}
